import FormField from "../../components/FormField";
import TextInput from "../../components/TextInput";
import TextArea from "../../components/TextArea";
import Select from "../../components/Select";
import DaysEditor from "./DaysEditor";
import type { ParcoursDto } from "../../../api/parcoursApi";

const COUNTRY_OPTIONS = ["Belgique"];

const BELGIAN_REGIONS = [
  "Région de Bruxelles-Capitale",
  "Région flamande",
  "Région wallonne",
];

interface Props {
  value: ParcoursDto;
  onChange: (next: ParcoursDto) => void;
  canEditPaid: boolean;
}

export default function ParcoursFormFields({ value, onChange, canEditPaid }: Props) {
  const set = <K extends keyof ParcoursDto>(key: K, v: ParcoursDto[K]) => onChange({ ...value, [key]: v });

  return (
    <div className="bo-stack">
      <FormField label="Titre" required>
        <TextInput value={value.title ?? ""} onChange={(e) => set("title", e.target.value)} required />
      </FormField>

      <FormField label="Description courte" hint="Résumé affiché sur les cartes et en haut de la fiche publique.">
        <TextArea value={value.description ?? ""} onChange={(e) => set("description", e.target.value)} rows={4} />
      </FormField>

      <FormField label="Image de couverture (URL)">
        <TextInput value={value.imageLink ?? ""} onChange={(e) => set("imageLink", e.target.value)} />
      </FormField>

      <fieldset className="bo-fieldset">
        <legend>Contenu principal de la fiche</legend>
        <FormField
          label="Type de contenu"
          hint="Choisissez ce qui est affiché sous l'onglet « Programme » de la fiche publique."
        >
          <Select
            value={value.contentMode ?? "PROGRAMME"}
            onChange={(e) => set("contentMode", e.target.value as "PROGRAMME" | "DESCRIPTION")}
          >
            <option value="PROGRAMME">Programme — liste horaire (onglet Programmes)</option>
            <option value="DESCRIPTION">Description longue — texte libre</option>
          </Select>
        </FormField>
        {value.contentMode === "DESCRIPTION" && (
          <FormField
            label="Description longue"
            hint="Utilisée à la place du programme. Les sauts de ligne sont conservés."
          >
            <TextArea
              value={value.longDescription ?? ""}
              onChange={(e) => set("longDescription", e.target.value)}
              rows={10}
              placeholder="Présentez le parcours en quelques paragraphes : ambiance, temps forts, invités, …"
            />
          </FormField>
        )}
      </fieldset>

      <fieldset className="bo-fieldset">
        <legend>Créneaux du parcours</legend>
        <p style={{ fontSize: 12, color: "var(--ink-mute)", margin: "0 0 var(--sp-3)" }}>
          Chaque créneau a une date de début (obligatoire) et une date de fin (optionnelle,
          pour un créneau qui s'étale sur plusieurs jours). Les heures de début / fin sont
          également optionnelles. Les programmes se rattachent à ces créneaux par leur date.
        </p>
        <DaysEditor value={value.dates ?? []} onChange={(next) => set("dates", next)} />
      </fieldset>

      <div className="bo-grid-3">
        <FormField label="Pays">
          <Select value={value.country ?? "Belgique"} onChange={(e) => set("country", e.target.value)}>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Région">
          <Select value={value.region ?? ""} onChange={(e) => set("region", e.target.value || undefined)}>
            <option value="">— Choisir —</option>
            {BELGIAN_REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Commune principale">
          <TextInput value={value.mainCommune ?? ""} onChange={(e) => set("mainCommune", e.target.value)} />
        </FormField>
      </div>

      <FormField label="Tarif (optionnel)" hint="Montant en euros">
        <TextInput
          type="number"
          step="0.01"
          value={value.price?.toString() ?? ""}
          onChange={(e) => set("price", e.target.value === "" ? undefined : Number(e.target.value))}
        />
      </FormField>

      <fieldset className="bo-fieldset">
        <legend>Infos pratiques (affichées sur la fiche publique)</legend>
        <FormField label="Accessibilité" hint="Ex. PMR, parking, transports">
          <TextArea value={value.accessibility ?? ""} onChange={(e) => set("accessibility", e.target.value)} rows={3} />
        </FormField>
        <FormField label="Contact principal" hint="Ex. Marie Dubois · 0470/12 34 56">
          <TextInput value={value.contact ?? ""} onChange={(e) => set("contact", e.target.value)} />
        </FormField>
        <FormField label="Contact secondaire">
          <TextInput value={value.secondaryContact ?? ""} onChange={(e) => set("secondaryContact", e.target.value)} />
        </FormField>
        <FormField label="Site web">
          <TextInput
            type="url"
            value={value.website ?? ""}
            onChange={(e) => set("website", e.target.value)}
            placeholder="https://…"
          />
        </FormField>
      </fieldset>

      <FormField
        label="Visibilité"
        hint={
          canEditPaid
            ? "Payant : tout visible publiquement. Gratuit : artistes et lieux masqués aux visiteurs non connectés."
            : "Seul l'administrateur peut modifier la visibilité du parcours."
        }
      >
        <Select
          value={value.paid ? "paid" : "free"}
          onChange={(e) => set("paid", e.target.value === "paid")}
          disabled={!canEditPaid}
        >
          <option value="free">Gratuit — artistes/lieux masqués aux anonymes</option>
          <option value="paid">Payant — tout visible publiquement</option>
        </Select>
      </FormField>
    </div>
  );
}
